import React, { useState, useEffect } from 'react'

import { Modal, Box, TextField, Autocomplete } from '@mui/material'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import { handleInteger, handleZero, deleteZero, handleNumber, canViewPrice, isValid, formatCurrency } from '../../helpers/helper'
import { useAuth } from '../../hooks/auth'

const findMatchingPricingTier = (item, customerId, quantity) => {
    const tiers = item?.pricing_tiers ?? []
    const qtyNumber = Number(quantity) || 0
    const today = dayjs()

    return tiers.find((tier) => {
        const sameCustomer = Number(tier.customer_id) === Number(customerId)
        const startedAlready = !isValid(tier.effective_from) || !today.isBefore(dayjs(tier.effective_from), 'day')
        const notExpiredYet = !isValid(tier.effective_to) || !today.isAfter(dayjs(tier.effective_to), 'day')
        const aboveMinQuantity = !isValid(tier.min_quantity) || qtyNumber >= Number(tier.min_quantity)
        const belowMaxQuantity = !isValid(tier.max_quantity) || qtyNumber <= Number(tier.max_quantity)

        return sameCustomer && startedAlready && notExpiredYet && aboveMinQuantity && belowMaxQuantity
    })
}

const style = {
    minWidth: "360px",
    maxWidth: "540px",
    width: "50%",
    height: "auto",
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: '#ffffff',
    border: 'transparent',
    borderRadius: '5px',
    boxShadow: 24,
    zIndex: "1600",
    outline: 'none'
}

const COST_TEXTFIELD_STYLE = {
    width: '100%',
    background: '#FCFCFC',
    '.MuiOutlinedInput-input': {
        padding: '9px !important'
    },
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': {
        border: '1px solid #EEEFF2 !important'
    }
}

const AddQuoteItem = ({ open, handleOpen, quoteItems, setQuoteItems, disabled, setDisabled, editingItem = null, customerId = '' }) => {

    const { user } = useAuth({ middleware: 'guest' })
    const { t } = useTranslation()
    const { config, features, company_id } = useStateContext()

    /* state */
    const [items, setItems] = useState([])
    const [vendors, setVendors] = useState([])
    const [count, setCount] = useState(() => dayjs().valueOf())
    const [selectedItem, setSelectedItem] = useState([])

    /* form */
    const [id, setId] = useState('')
    const [no, setNo] = useState('')
    const [description, setDescription] = useState(' ')
    const [uom, setUom] = useState('')
    const [qty, setQty] = useState('')
    const [sourceVendorId, setSourceVendorId] = useState('')
    const [sourceVendorName, setSourceVendorName] = useState('')
    const [sourceCost, setSourceCost] = useState('')
    const [freightCost, setFreightCost] = useState('')
    const [fuelSurcharge, setFuelSurcharge] = useState('')
    const [otherCosts, setOtherCosts] = useState('')
    const [unitPrice, setUnitPrice] = useState('')

    useEffect(() => {
        if (open) {
            getItems(quoteItems.map(({ item_no }) => item_no))
            getVendors(company_id)
        }
    }, [open, quoteItems])

    useEffect(() => {
        if (open && editingItem) {
            setId(editingItem.item_id || '')
            setQty(editingItem.quantity ?? '')
            setSourceVendorId(editingItem.source_vendor_id || '')
            setSourceVendorName(editingItem.source_vendor_name || '')
            setSourceCost(editingItem.source_cost ?? '')
            setFreightCost(editingItem.freight_cost ?? '')
            setFuelSurcharge(editingItem.fuel_surcharge ?? '')
            setOtherCosts(editingItem.other_costs ?? '')
            setUnitPrice(editingItem.unit_price ?? '')
        }
    }, [open, editingItem])

    useEffect(() => {
        if (open) {
            if (id) {
                getItem(id)
            } else {
                setNo('')
                setDescription('')
                setUom('')
                setDisabled(true)
            }
        }
    }, [id])

    useEffect(() => {
        if (!Array.isArray(selectedItem)) {
            setNo(selectedItem?.item_no)
            setDescription(selectedItem?.description)
            setUom(selectedItem?.unit_of_measure_name ?? '')
        }
    }, [selectedItem])

    useEffect(() => {
        if (open && !editingItem && !Array.isArray(selectedItem)) {
            const matchingTier = findMatchingPricingTier(selectedItem, customerId, qty)
            setUnitPrice(matchingTier ? String(matchingTier.unit_price) : '')
        }
    }, [open, editingItem, selectedItem, customerId, qty])

    /* standard cost defaults from the item master; left blank for manual entry if not set there */
    useEffect(() => {
        if (open && !editingItem && !Array.isArray(selectedItem)) {
            setSourceCost(isValid(selectedItem?.standard_cost) ? String(selectedItem.standard_cost) : '')
        }
    }, [open, editingItem, selectedItem])

    /* getters */
    const getItems = async (without = []) => {
        await axios
            .get(`/api/items?blocked_for_sales=0&blocked=0&company_id=${localStorage.getItem('company_id')}&with=quotePricingTiers`, config)
            .then(res => {
                const items = res.data
                const filteredItems = items.filter(item => {
                    return !without.includes(item['item_no'])
                })
                setItems(filteredItems)
            })
    }

    const getItem = async (id) => {
        await axios
            .get(`/api/items/${id}?with=unit_of_measure,quotePricingTiers`, config)
            .then(res => {
                const item = res.data
                setSelectedItem(item)
                setDisabled(false)
            })
    }

    const getVendors = async (id) => {
        await axios
            .get(`/api/vendors?company_id=${id}&blocked=0`, config)
            .then(res => {
                setVendors(res.data)
            })
    }

    /* handlers */
    const closeAndCleanUp = () => {
        handleOpen()
        setId('')
        setNo('')
        setDescription(' ')
        setUom('')
        setQty('')
        setSourceVendorId('')
        setSourceVendorName('')
        setSourceCost('')
        setFreightCost('')
        setFuelSurcharge('')
        setOtherCosts('')
        setUnitPrice('')
        setDisabled(true)
    }

    const handleSourceVendorChange = (a, b) => {
        setSourceVendorId(b?.id || '')
        setSourceVendorName(b?.label || '')
    }

    const isFormValid = () => (
        isValid(id)
        && qty !== '' && Number(qty) > 0
        && (!canViewPrice(user, features) || (unitPrice !== '' && !isNaN(Number(unitPrice))))
    )

    const totalCost = ((Number(sourceCost) || 0) * (Number(qty) || 0)) + (Number(freightCost) || 0) + (Number(fuelSurcharge) || 0) + (Number(otherCosts) || 0)
    const totalPrice = (Number(qty) || 0) * (Number(unitPrice) || 0)
    const marginDollar = totalPrice - totalCost
    const marginPercent = totalPrice > 0 ? (marginDollar / totalPrice) * 100 : 0
    const isNegativeMargin = marginDollar < 0

    const createItem = async (e) => {
        e.preventDefault();
        
        const item_payload = {
            "item_no": no,
            "item_id": selectedItem.id,
            "description": description,
            "quantity": Number(qty),
            "unit_id": selectedItem.unit_of_measure_id,
            "unit_of_measure_code": selectedItem?.unit_of_measure_name,
            "source_vendor_id": sourceVendorId,
            "source_vendor_name": sourceVendorName,
            "source_cost": (Number(sourceCost) || 0).toFixed(2),
            "freight_cost": (Number(freightCost) || 0).toFixed(2),
            "fuel_surcharge": (Number(fuelSurcharge) || 0).toFixed(2),
            "other_costs": (Number(otherCosts) || 0).toFixed(2),
            "calculated_cost": totalCost.toFixed(2),
            "unit_price": (Number(unitPrice) || 0).toFixed(2),
            "total_price": totalPrice.toFixed(2),
            "margin_dollar": marginDollar.toFixed(2),
            "margin_percent": marginPercent.toFixed(2)
        }

        if (editingItem) {
            setQuoteItems((prev) => prev.map((item) => item.id === editingItem.id ? { ...item_payload, id: editingItem.id } : item))
        } else {
            setQuoteItems((prev) => [...prev, { ...item_payload, id: count }])
            setCount(prev => prev + 1)
        }

        closeAndCleanUp()
    }

    const options = items?.map(option => ({ no: option.item_no, id: option.id, description: option.description }))
    const optionsVendors = vendors?.map(option => ({ label: option.vendor_no + ' - ' + option.name, id: option.id }))

    return (
        <Modal open={open} onClose={closeAndCleanUp}>
            <Box sx={style}>
                <div className='flex justify-between items-center p-5 w-full' style={{ backgroundColor: '#336195', borderRadius: '5px 5px 0 0' }}>
                    <div className='flex items-baseline justify-between w-full'>
                        <p className='text-[18px] font-[600] text-white'>{editingItem ? t('edit_quote_item') : t('add_quote_item')}</p>
                        <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-white">
                            <button onClick={closeAndCleanUp}><i className="fa-solid fa-plus"></i></button>
                        </div>
                    </div>
                </div>
                <div>
                    <div className='p-5' style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                        <form className='flex justify-center flex-col items-start pt-1'>
                            <div className='flex gap-5 w-full pb-4'>
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('item__no')} *</p>
                                    {editingItem ? (
                                        <TextField
                                            sx={COST_TEXTFIELD_STYLE}
                                            type="text"
                                            name="item_no"
                                            value={no}
                                            disabled
                                        />
                                    ) : (
                                        <Autocomplete
                                            disablePortal
                                            id="item-no-autocomplete"
                                            options={options}
                                            getOptionLabel={(option) => option?.no ?? ''}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            sx={{
                                                width: '100%',
                                                background: '#FCFCFC',
                                                '.MuiOutlinedInput-input': {
                                                    padding: '0 !important'
                                                },
                                                boxShadow: 'none',
                                                '.MuiOutlinedInput-notchedOutline': {
                                                    border: '1px solid #EEEFF2 !important'
                                                }
                                            }}
                                            onChange={(a, b) => setId(b?.id || '')}
                                            renderInput={(params) => <TextField {...params} autoFocus={true} />}
                                        />
                                    )}
                                </div>
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('vendor')}</p>
                                    <Autocomplete
                                        disablePortal
                                        id="source-vendor-autocomplete"
                                        options={optionsVendors}
                                        value={sourceVendorId ? { label: sourceVendorName, id: sourceVendorId } : null}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        disabled={disabled}
                                        sx={{
                                            width: '100%',
                                            background: '#FCFCFC',
                                            '.MuiOutlinedInput-input': {
                                                padding: '0 !important'
                                            },
                                            boxShadow: 'none',
                                            '.MuiOutlinedInput-notchedOutline': {
                                                border: '1px solid #EEEFF2 !important'
                                            }
                                        }}
                                        onChange={handleSourceVendorChange}
                                        renderInput={(params) => <TextField {...params} />}
                                    />
                                </div>
                            </div>

                            <div className='flex gap-5 w-full pb-4'>
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('description')}</p>
                                    <TextField
                                        sx={COST_TEXTFIELD_STYLE}
                                        type="text"
                                        name="description"
                                        value={description}
                                        disabled
                                    />
                                </div>
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('uom')}</p>
                                    <TextField
                                        sx={COST_TEXTFIELD_STYLE}
                                        type="text"
                                        name="uom"
                                        value={uom}
                                        disabled
                                    />
                                </div>
                            </div>

                            <div className='flex gap-5 w-full pb-4'>
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">{t('qty')} *</p>
                                    <TextField
                                        sx={COST_TEXTFIELD_STYLE}
                                        type="text"
                                        name="qty"
                                        value={qty}
                                        onChange={(e) => handleInteger({
                                            event: e,
                                            setter: setQty
                                        })}
                                        onBlur={e => handleZero({
                                            event: e,
                                            setter: setQty
                                        })}
                                        onFocus={e => deleteZero({
                                            event: e,
                                            setter: setQty,
                                            value: qty
                                        })}
                                        disabled={disabled}
                                    />
                                </div>
                                {canViewPrice(user, features) && (
                                    <div className='w-full'>
                                        <p className="text-[14px] text-[#718096]">{t('unit_price')} *</p>
                                        <TextField
                                            sx={COST_TEXTFIELD_STYLE}
                                            type="text"
                                            name="unitPrice"
                                            value={unitPrice}
                                            onChange={(e) => handleNumber({ event: e, setter: setUnitPrice, precision: 2 })}
                                            onBlur={e => handleZero({ event: e, setter: setUnitPrice })}
                                            onFocus={e => deleteZero({ event: e, setter: setUnitPrice, value: unitPrice })}
                                            disabled={disabled}
                                        />
                                    </div>
                                )}
                            </div>

                            {
                                canViewPrice(user, features) && (
                                    <>
                                        <div className='flex gap-5 w-full pb-4'>
                                            <div className='w-full'>
                                                <p className="text-[14px] text-[#718096]">{t('source_cost')}</p>
                                                <TextField
                                                    sx={COST_TEXTFIELD_STYLE}
                                                    type="text"
                                                    name="sourceCost"
                                                    value={sourceCost}
                                                    onChange={(e) => handleNumber({ event: e, setter: setSourceCost, precision: 2 })}
                                                    onBlur={e => handleZero({ event: e, setter: setSourceCost })}
                                                    onFocus={e => deleteZero({ event: e, setter: setSourceCost, value: sourceCost })}
                                                    disabled={disabled}
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <p className="text-[14px] text-[#718096]">{t('freight_cost')}</p>
                                                <TextField
                                                    sx={COST_TEXTFIELD_STYLE}
                                                    type="text"
                                                    name="freightCost"
                                                    value={freightCost}
                                                    onChange={(e) => handleNumber({ event: e, setter: setFreightCost, precision: 2 })}
                                                    onBlur={e => handleZero({ event: e, setter: setFreightCost })}
                                                    onFocus={e => deleteZero({ event: e, setter: setFreightCost, value: freightCost })}
                                                    disabled={disabled}
                                                />
                                            </div>
                                        </div>

                                        <div className='flex gap-5 w-full pb-4'>
                                            <div className='w-full'>
                                                <p className="text-[14px] text-[#718096]">{t('fuel_surcharge')}</p>
                                                <TextField
                                                    sx={COST_TEXTFIELD_STYLE}
                                                    type="text"
                                                    name="fuelSurcharge"
                                                    value={fuelSurcharge}
                                                    onChange={(e) => handleNumber({ event: e, setter: setFuelSurcharge, precision: 2 })}
                                                    onBlur={e => handleZero({ event: e, setter: setFuelSurcharge })}
                                                    onFocus={e => deleteZero({ event: e, setter: setFuelSurcharge, value: fuelSurcharge })}
                                                    disabled={disabled}
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <p className="text-[14px] text-[#718096]">{t('other_costs')}</p>
                                                <TextField
                                                    sx={COST_TEXTFIELD_STYLE}
                                                    type="text"
                                                    name="otherCosts"
                                                    value={otherCosts}
                                                    onChange={(e) => handleNumber({ event: e, setter: setOtherCosts, precision: 2 })}
                                                    onBlur={e => handleZero({ event: e, setter: setOtherCosts })}
                                                    onFocus={e => deleteZero({ event: e, setter: setOtherCosts, value: otherCosts })}
                                                    disabled={disabled}
                                                />
                                            </div>
                                        </div>

                                        <div className='flex gap-5 w-full pb-4'>
                                            <div className='w-full'>
                                                <p className="text-[14px] text-[#718096]">{t('calculated_cost')}</p>
                                                <TextField
                                                    sx={COST_TEXTFIELD_STYLE}
                                                    type="text"
                                                    name="totalCost"
                                                    value={formatCurrency(totalCost)}
                                                    disabled
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <p className="text-[14px] text-[#718096]">{t('total_price')}</p>
                                                <TextField
                                                    sx={COST_TEXTFIELD_STYLE}
                                                    type="text"
                                                    name="totalPrice"
                                                    value={formatCurrency(totalPrice)}
                                                    disabled
                                                />
                                            </div>
                                        </div>

                                        <div className='flex gap-5 w-full pb-5'>
                                            <div className='w-full'>
                                                <p className="text-[14px] text-[#718096]">{t('margin_dollar')}</p>
                                                <TextField
                                                    sx={COST_TEXTFIELD_STYLE}
                                                    type="text"
                                                    name="marginDollar"
                                                    value={formatCurrency(marginDollar)}
                                                    disabled
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <p className="text-[14px] text-[#718096]">{t('margin_percent')}</p>
                                                <TextField
                                                    sx={{
                                                        ...COST_TEXTFIELD_STYLE,
                                                        ...(isNegativeMargin ? { '.MuiOutlinedInput-notchedOutline': { border: '1px solid #FFA726 !important' } } : {})
                                                    }}
                                                    type="text"
                                                    name="marginPercent"
                                                    value={`${formatCurrency(marginPercent)}%`}
                                                    disabled
                                                />
                                                {isNegativeMargin && (
                                                    <p className='text-[12px] mt-1' style={{ color: '#FFA726' }}>
                                                        {t('negative_margin_warning')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )
                            }
                        </form>
                        <div className='w-full flex gap-2 justify-between'>
                            <button type="button" onClick={closeAndCleanUp} className='w-full text-[14px] text-[#919399] rounded-sm px-4 py-2 bg-[#F3F5F7] hover:bg-[#E6E6E6] disabled:bg-[#B8B7BC] capitalize'>
                                {t('cancel')}
                            </button>
                            <button type="button" onClick={createItem} disabled={disabled || !isFormValid()} className='w-full text-[14px] text-white rounded-sm px-4 py-2 bg-[#015D9F] hover:bg-[#003459] disabled:bg-[#B8B7BC] capitalize'>
                                {editingItem ? t('save') : t('add')}
                            </button>
                        </div>
                    </div>
                </div>
            </Box>
        </Modal>
    )
}

export default AddQuoteItem
